import React, { useState, useCallback, useEffect } from 'react';
import { Button, Alert, DateInput } from '../ui';
import { useAppointments } from '../../contexts/AppointmentContext';
import { useSalon } from '../../contexts/SalonContext';
import { useProfessionals } from '../../contexts/ProfessionalContext';
import { useServices } from '../../contexts/ServiceContext';
import { formatDate } from '../../utils/dateUtils';
import { calculateTotalValue } from '../../utils/priceUtils';
import { AppointmentTimeline } from '../../components/booking/timeline';
import AppointmentDayTable from '../booking/AppointmentDayTable';
import AppointmentModal from '../AppointmentModal';
import DeleteConfirmationModal from '../shared/DeleteConfirmationModal';
import { useWindowSize } from '../../hooks';
import { useTutorial } from '../../hooks/useTutorial';
import MobileTutorial from '../onboarding/MobileTutorial';
import DesktopMultiStepForm from './DesktopMultiStepForm';
import { AdminBookingForm } from './booking';
import AppointmentSearch from './AppointmentSearch';
import AppointmentSearchResults from './AppointmentSearchResults';
import useAppointmentSearch from '../../hooks/useAppointmentSearch';
import AppointmentDetailModal from './AppointmentDetailModal';

const AppointmentManagement = ({ selectedDate, onDateChange }) => {
  const { 
    appointments, 
    loading, 
    error, 
    addAppointment, 
    updateAppointment, 
    removeAppointment, 
    confirmAppointmentCtx,
    cancelAppointmentCtx,
    markMessageSent
  } = useAppointments();
  
  const { salonConfig } = useSalon();
  const { professionals } = useProfessionals();
  const { services } = useServices();
  const { width } = useWindowSize();
  const isMobile = width < 768; // Considera dispositivos com menos de 768px como mobile
  const { shouldShowTutorial, completeTutorial } = useTutorial();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAppointment, setCurrentAppointment] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  const [isSearchMode, setIsSearchMode] = useState(false);
  
  // Estado para controlar o modal de detalhes
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  // Hook de pesquisa de agendamentos
  const { 
    searchResults, 
    totalResults,
    applyFilters, 
    clearFilters, 
    loading: searchLoading 
  } = useAppointmentSearch({ startDate: selectedDate });
  
  // Filtra os agendamentos pelo dia atual
  const todayAppointments = appointments
    .filter(app => {
      // Converter datas para o mesmo formato para comparação
      let dataApp, dataSelecionada;
      
      // Converter data do agendamento para formato ISO
      if (typeof app.data === 'string') {
        dataApp = app.data;
      } else if (app.data instanceof Date) {
        dataApp = formatDate(app.data);
      } else {
        return false; // Formato inválido, ignorar
      }
      
      // Converter data selecionada para formato ISO 
      if (typeof selectedDate === 'string') {
        dataSelecionada = selectedDate;
      } else if (selectedDate instanceof Date) {
        dataSelecionada = formatDate(selectedDate);
      } else {
        return false; // Formato inválido, ignorar
      }
      
      return dataApp === dataSelecionada;
    })
    .sort((a, b) => {
      // Ordenar por horário
      const timeA = a.horario.split(':').map(Number);
      const timeB = b.horario.split(':').map(Number);
      
      if (timeA[0] !== timeB[0]) {
        return timeA[0] - timeB[0]; // Ordenar por hora
      }
      return timeA[1] - timeB[1]; // Se mesma hora, ordenar por minuto
    });

  // Handlers
  const handleNewAppointment = () => {
    setCurrentAppointment(null);
    setIsModalOpen(true);
  };

  const handleEditAppointment = (appointment) => {
    setCurrentAppointment(appointment);
    setIsModalOpen(true);
  };

  const handleRescheduleAppointment = (appointment) => {
    const appointmentWithFlag = {
      ...appointment,
      _isReschedule: true
    };
    setCurrentAppointment(appointmentWithFlag);
    setIsModalOpen(true);
  };

  const handleToggleStatus = async (appointment, isConfirming) => {
    try {
      await updateAppointment(appointment.id, { 
        confirmado: isConfirming,
        cancelado: !isConfirming
      });
    } catch (err) {
      console.error('Erro ao atualizar status do agendamento:', err);
    }
  };

  const handleDeleteConfirm = (appointment) => {
    setAppointmentToDelete(appointment);
    setConfirmModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!appointmentToDelete) return;
    
    try {
      await removeAppointment(appointmentToDelete.id);
      setConfirmModalOpen(false);
      setAppointmentToDelete(null);
    } catch (err) {
      console.error('Erro ao excluir agendamento:', err);
    }
  };
  
  // Função para formatar os dados de agendamento para o modal
  const formatAppointmentForModal = (appointment) => {
    if (!appointment) {
      // Novo agendamento
      const today = new Date();
      const day = String(today.getDate()).padStart(2, '0');
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const year = today.getFullYear();
      const dataBR = `${day}/${month}/${year}`;
      const dataISO = `${year}-${month}-${day}`;
      const time = `${today.getHours().toString().padStart(2, '0')}:${today.getMinutes().toString().padStart(2, '0')}`;
      
      return {
        cliente: '',
        whatsapp: '',
        email: '',
        data: dataISO, // Para o MultiStepForm, usamos formato ISO como principal
        dataISO: dataISO,
        dataFormatada: dataBR,
        dataString: dataBR,
        horario: time,
        servicos: [],
        profissional: ''
      };
    } else {
      // Edição de agendamento existente
      // Garantir que tanto a data ISO quanto a formatada estejam disponíveis
      let dataISO = appointment.data;
      let dataFormatada = appointment.dataFormatada || appointment.dataString;
      
      // Se a data estiver no formato brasileiro, converter para ISO
      if (typeof dataISO === 'string' && dataISO.includes('/')) {
        const parts = dataISO.split('/');
        dataISO = `${parts[2]}-${parts[1]}-${parts[0]}`;
        dataFormatada = dataISO;
      } 
      // Se a data estiver no formato ISO, gerar a versão formatada
      else if (typeof dataISO === 'string' && dataISO.includes('-')) {
        const parts = dataISO.split('-');
        if (parts.length === 3) {
          dataFormatada = `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
      }
      
      return {
        ...appointment,
        // Garantir que data esteja no formato ISO para o MultiStepForm
        data: dataISO,
        dataISO: dataISO, 
        dataFormatada: dataFormatada,
        dataString: dataFormatada,
        // Converter os serviços para o formato esperado pelo modal
        servicos: Array.isArray(appointment.servicos) ? 
          appointment.servicos.map(s => typeof s === 'object' ? s.id : s) : 
          (appointment.servicosIds || [])
      };
    }
  };
  
  // Função para adicionar ou atualizar um agendamento
  const handleSubmitAppointment = async (appointmentData) => {
    try {
      if (currentAppointment) {
        // Atualizar agendamento existente
        await updateAppointment(currentAppointment.id, appointmentData);
      } else {
        // Calcular duração total baseada nos serviços selecionados
        let duracaoTotal = 0;
        const servicosDetalhes = [];
        
        // Criar o array de serviços formatado corretamente
        const servicosArray = appointmentData.servicos.map(serviceId => {
          const service = services.find(s => s.id === serviceId);
          if (service) {
            // Adicionar duração + setup + finish time
            const setupTime = service?.setupTime || 0;
            const finishTime = service?.finishTime || 0;
            const duration = service.duracao || 30;
            
            duracaoTotal += (duration + setupTime + finishTime);
            
            // Adicionar detalhes do serviço
            servicosDetalhes.push({
              id: service.id,
              nome: service.nome,
              preco: service.preco,
              duracao: duration
            });
            
            // Formato esperado
            return {
              id: serviceId,
              nome: service.nome,
              valor: service.preco || 0,
              duracao: duration
            };
          }
          return null;
        }).filter(Boolean);
        
        // Formatar o whatsapp (remover formatação)
        const formattedWhatsapp = appointmentData.whatsapp.replace(/\D/g, '').slice(0, 11);
        
        // Garantir que o profissional está no formato correto
        let profissionalFormatado = appointmentData.profissional;
        if (typeof profissionalFormatado === 'string') {
          const profissional = professionals.find(p => p.id === profissionalFormatado);
          if (profissional) {
            profissionalFormatado = {
              id: profissional.id,
              nome: profissional.nome
            };
          }
        }
        
        // Calcular horário de fim baseado na duração total
        const [hoursStart, minutesStart] = appointmentData.horario.split(':').map(Number);
        const startTime = new Date();
        startTime.setHours(hoursStart, minutesStart, 0, 0);
        
        const endTime = new Date(startTime.getTime() + (duracaoTotal * 60 * 1000));
        const horarioFim = `${endTime.getHours().toString().padStart(2, '0')}:${endTime.getMinutes().toString().padStart(2, '0')}`;
        
        // Processar data de forma padronizada
        // Detectar formato da data: ISO (YYYY-MM-DD) ou BR (DD/MM/YYYY)
        let year, month, day, dataFormatada, dataString;
        const dataInput = appointmentData.data || '';
        
        if (dataInput.includes('-')) {
          // Formato ISO
          [year, month, day] = dataInput.split('-').map(Number);
          dataFormatada = dataInput;
          dataString = appointmentData.dataFormatada || `${day}/${month}/${year}`;
        } else if (dataInput.includes('/')) {
          // Formato BR
          [day, month, year] = dataInput.split('/').map(Number);
          dataFormatada = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          dataString = dataInput;
        } else {
          // Fallback para data atual
          const hoje = new Date();
          year = hoje.getFullYear();
          month = hoje.getMonth() + 1;
          day = hoje.getDate();
          dataFormatada = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          dataString = `${day}/${month}/${year}`;
        }
        
        // Criar objeto Date para timestamp
        const appointmentDate = new Date(year, month - 1, day);
        appointmentDate.setHours(hoursStart, minutesStart, 0, 0);
        
        // Verificar se a data é hoje
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const appointmentDay = new Date(appointmentDate);
        appointmentDay.setHours(0, 0, 0, 0);
        const isToday = today.getTime() === appointmentDay.getTime();
        
        // Preparar dados completos
        const appointmentDbData = {
          cliente: appointmentData.cliente,
          whatsapp: formattedWhatsapp,
          email: appointmentData.email || '',
          data: dataFormatada,
          dataTimestamp: appointmentDate,
          dataString: dataString,
          horario: appointmentData.horario.substring(0, 5),
          horarioFim: horarioFim,
          profissional: profissionalFormatado,
          servicos: servicosArray,
          servicosIds: appointmentData.servicos,
          servicosDetalhes: servicosDetalhes,
          duracaoTotal: duracaoTotal,
          duracaoEfetiva: duracaoTotal,
          valorTotal: calculateTotalValue(appointmentData.servicos, services),
          // Status baseados nas regras de negócio
          confirmado: appointmentData.confirmado !== undefined ? appointmentData.confirmado : isToday,
          pagamentoConfirmado: appointmentData.pagamentoConfirmado || false, // Sempre falso por padrão, a menos que explicitamente definido
          cancelado: false,
          origem: 'admin',
          deviceType: 'admin',
          createdAt: new Date(),
          observacoes: appointmentData.observacoes || ''
        };
        
        // Adicionar o agendamento
        await addAppointment(appointmentDbData);
      }
      
      // Fechar o modal
      setIsModalOpen(false);
      setCurrentAppointment(null);
      
    } catch (error) {
      console.error('Erro ao salvar agendamento:', error);
      alert('Erro ao salvar agendamento. Por favor, tente novamente.');
    }
  };
  
  // Função para formatar data para exibição
  const formatarDataParaExibicao = (data) => {
    try {
      if (typeof data === 'string') {
        if (data.includes('-')) {
          // Formato ISO (YYYY-MM-DD)
          return data.split('-').reverse().join('/');
        } else if (data.includes('/')) {
          // Já está no formato brasileiro
          return data;
        }
      } else if (data instanceof Date) {
        const dia = String(data.getDate()).padStart(2, '0');
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const ano = data.getFullYear();
        return `${dia}/${mes}/${ano}`;
      }
      
      return new Date().toLocaleDateString('pt-BR');
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return new Date().toLocaleDateString('pt-BR');
    }
  };
  
  // Função para alternar entre modo de pesquisa e visualização normal
  const toggleSearchMode = useCallback(() => {
    const newMode = !isSearchMode;
    setIsSearchMode(newMode);
    
    // Ao entrar no modo de pesquisa, aplicar o filtro com a data selecionada
    if (newMode) {
      applyFilters({ startDate: selectedDate });
    } else {
      // Ao sair do modo de pesquisa, limpar filtros
      clearFilters();
    }
  }, [isSearchMode, selectedDate, applyFilters, clearFilters]);
  
  // Atualizar filtros quando a data selecionada muda enquanto está no modo de pesquisa
  useEffect(() => {
    // Aplicar o filtro apenas se já estiver no modo de pesquisa (não no primeiro render)
    if (isSearchMode) {
      // Usar um timeout para evitar chamadas múltiplas em sequência
      const timer = setTimeout(() => {
        applyFilters({ startDate: selectedDate });
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [selectedDate]);
  
  // Visualizar detalhes do agendamento
  const handleViewAppointment = useCallback((appointment) => {
    setSelectedAppointment(appointment);
    setDetailModalOpen(true);
  }, []);
  
  if (loading) {
    return <div className="p-4">Carregando agendamentos...</div>;
  }

  if (error) {
    return <Alert type="error" title="Erro ao carregar agendamentos">{error}</Alert>;
  }

  return (
    <div className="flex flex-col h-full space-y-4">
      {/* Tutorial Mobile */}
      {isMobile && shouldShowTutorial && (
        <MobileTutorial onComplete={completeTutorial} />
      )}

      {/* Cabeçalho com controles - exibido apenas em desktop */}
      {!isMobile && (
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
          <h2 className="text-2xl font-bold text-gray-800">Agenda</h2>
          
          <div className="flex space-x-2">
            <Button 
              onClick={toggleSearchMode} 
              variant={isSearchMode ? "primary" : "outline"}
              className="whitespace-nowrap"
            >
              {isSearchMode ? "Voltar à Agenda" : "Buscar Agendamentos"}
            </Button>
            
            <Button 
              onClick={handleNewAppointment} 
              variant="primary"
              className="whitespace-nowrap"
            >
              Novo Agendamento
            </Button>
          </div>
        </div>
      )}

      {/* Componente de busca - visível apenas quando isSearchMode é true */}
      {isSearchMode && (
        <AppointmentSearch 
          onSearch={applyFilters} 
          defaultDate={selectedDate}
          professionals={professionals}
        />
      )}

      {/* Visualização principal - timeline ou resultados de pesquisa */}
      {isSearchMode ? (
        <AppointmentSearchResults
          appointments={searchResults}
          isLoading={searchLoading}
          onView={handleViewAppointment}
          onEdit={handleEditAppointment}
          onReschedule={handleRescheduleAppointment}
          onDelete={handleDeleteConfirm}
          onConfirm={(appointment) => handleToggleStatus(appointment, true)}
          onCancel={(appointment) => handleToggleStatus(appointment, false)}
        />
      ) : (
        <div className="flex-grow bg-white rounded-lg shadow-sm mb-4">
          <AppointmentTimeline
            appointments={appointments}
            selectedDate={selectedDate}
            professionals={professionals || []}
            salonConfig={salonConfig}
            onDateChange={onDateChange}
            onEventUpdate={async (appointmentId, updateData) => {
              try {
                const success = await updateAppointment(appointmentId, updateData);
                return success;
              } catch (error) {
                console.error('Erro ao atualizar agendamento no calendário:', error);
                return false;
              }
            }}
          />
        </div>
      )}
      
      {/* Lista compacta de agendamentos do dia - visível apenas quando não está em modo de pesquisa */}
      {todayAppointments.length > 0 && !isMobile && !isSearchMode && (
        <AppointmentDayTable
          appointments={todayAppointments.map(appointment => ({
            id: appointment.id,
            time: appointment.horario,
            client: {
              name: appointment.cliente,
              phone: appointment.whatsapp
            },
            service: Array.isArray(appointment.servicos) && appointment.servicos.length > 0
              ? (typeof appointment.servicos[0] === 'object' 
                ? appointment.servicos[0].nome 
                : services.find(s => s.id === appointment.servicos[0])?.nome || 'Serviço')
              : 'Serviço não especificado',
            professional: appointment.profissional 
              ? (typeof appointment.profissional === 'object' 
                  ? appointment.profissional.nome 
                  : professionals.find(p => p.id === appointment.profissional)?.nome || appointment.profissional)
              : 'Profissional não especificado',
            status: appointment.cancelado 
              ? 'Cancelado' 
              : appointment.confirmado 
                ? 'Confirmado' 
                : 'Pendente',
            // Dados originais necessários para as funções
            originalData: appointment
          }))}
          date={formatarDataParaExibicao(selectedDate)}
          onEdit={(appointmentData) => handleEditAppointment(appointmentData.originalData)}
          onReschedule={(appointmentData) => handleRescheduleAppointment(appointmentData.originalData)}
          onCancel={(appointmentData) => handleToggleStatus(appointmentData.originalData, false)}
          onConfirm={(appointmentData) => handleToggleStatus(appointmentData.originalData, true)}
          onDelete={(appointmentData) => handleDeleteConfirm(appointmentData.originalData)}
        />
      )}

      {/* Modal de novo agendamento ou edição */}
      {isMobile ? (
        <AdminBookingForm
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setCurrentAppointment(null);
          }}
          onSubmit={handleSubmitAppointment}
          initialData={formatAppointmentForModal(currentAppointment)}
          services={services}
          professionals={professionals}
          salonId={salonConfig?.id}
          mode={currentAppointment ? (currentAppointment._isReschedule ? 'reschedule' : 'edit') : 'new'}
        />
      ) : (
        <DesktopMultiStepForm
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setCurrentAppointment(null);
          }}
          onSubmit={handleSubmitAppointment}
          initialData={formatAppointmentForModal(currentAppointment)}
          services={services}
          professionals={professionals}
          salonId={salonConfig?.id}
          mode={currentAppointment ? 'edit' : 'new'}
        />
      )}
      
      {/* Modal de confirmação de exclusão */}
      <DeleteConfirmationModal
        isOpen={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setAppointmentToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Excluir Agendamento"
        message={`Tem certeza que deseja excluir o agendamento de ${appointmentToDelete?.cliente || 'este cliente'}? Esta ação não pode ser desfeita.`}
      />

      {/* Modal de detalhes do agendamento */}
      <AppointmentDetailModal 
        appointment={selectedAppointment}
        isOpen={detailModalOpen}
        onClose={() => {
          setDetailModalOpen(false);
          setSelectedAppointment(null);
        }}
        onReschedule={handleRescheduleAppointment}
        onCancel={(appointment) => handleToggleStatus(appointment, false)}
        onEditServices={handleEditAppointment}
        onMarkAsPaid={(appointment) => {
          // Implementar a lógica para marcar como pago
          updateAppointment(appointment.id, { 
            pagamentoConfirmado: true,
            pago: true
          });
        }}
      />
    </div>
  );
};

export default AppointmentManagement; 